@import "~@swisscom/sdx/stylesheets/sdx/variables/colors";
@import "~@swisscom/sdx/stylesheets/sdx/variables/sizes";
@import "~@swisscom/sdx/stylesheets/sdx/variables/grid";
@import "~@swisscom/sdx/stylesheets/sdx/mixins/breakpoints";
@import "~@swisscom/sdx/stylesheets/sdx/mixins/mediaqueries";
@import "~@swisscom/sdx/stylesheets/sdx/mixins/typography";
@import "~@swisscom/sdx/stylesheets/sdx/mixins/text";
@import "~@swisscom/sdx/stylesheets/sdx/mixins/misc";
@import "~@swisscom/sdx/stylesheets/sdx/mixins/core";
@import "~@swisscom/sdx/stylesheets/sdx/mixins/gradient";
@import "~@swisscom/sdx/stylesheets/sdx/mixins/button";
@import "~@swisscom/sdx/stylesheets/sdx/variables/global";
@import "~@swisscom/sdx/stylesheets/sdx/button/button";
@import "~@swisscom/sdx/stylesheets/sdx/images/images";
@import "~@swisscom/sdx/stylesheets/sdx/link/link";
@import "~@swisscom/sdx/stylesheets/sdx/typography/typography";

:host {
  padding-top: 0 !important;
  height: auto !important;
  display: inline !important;
}

.component {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: $color-white;
  border-radius: 20px;
  overflow: hidden; // clip border-radius
  text-decoration: none;
  border: 1px solid transparent;
  box-shadow:
    0 0 40px $color-aluminum,
    0 0 32px $color-aluminum,
    0 0 24px $color-aluminum,
    0 0 16px $color-aluminum;
  user-select: none;
  container-type: inline-size;

  // workaround for glitch during transition
  .sticker {
    z-index: 0;
    position: relative;

    .image {
      z-index: -1;
      position: relative;
    }
  }

  &.standard .first.first.first .link-wrapper {
    padding-top: $baseline;
  }

  .link-wrapper {
    display: flex;
    flex-direction: column;
    align-items: end;

    .link {
      color: $color-gray;
      background: rgb($color-white, 0.84);
      box-sizing: border-box;
      padding: 3px;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 1.5px solid;
      text-decoration: none;

      &::before {
        font-size: 24px;
        padding: 0;
        margin: 0;
      }
    }
  }

  // hide either first or second link button
  &.content-bottom,
  &:not(.has-image) {
    .first .link-wrapper {
      display: none;
    }
  }
  &.has-image:not(.content-bottom) .second .link-wrapper {
    display: none;
  }

  &.animated {
    transition: all 400ms $standard-easing;
  }

  // using @media(hover:hover) not possible when testing with puppeteer:
  // https://github.com/puppeteer/puppeteer/issues/5096
  &:hover:where(:not(.is-touchscreen)),
  &:focus-visible {
    &.card,
    &.product,
    &.product-out-of-stock,
    &.product-button {
      border-color: $color-gray-tint-4;
      box-shadow: 0 4px 20px 0 rgb($color-gray, 0.16);
    }

    .image {
      transition-duration: 800ms;
      transform: scale(1.05);
    }

    .link {
      @include link-hover;
      text-decoration-color: transparent;

      &.link--white {
        @include link-hover-dark;
      }
    }

    .button.button--secondary {
      @include button-hover(
        (
          button-color--active: $color-button-secondary--active,
          button-border-color--active: $color-int-blue--active,
        )
      );
    }
  }

  // common styles for first & second "half"
  .first {
    overflow: hidden;

    .image {
      display: block;
      aspect-ratio: 4/3;
      object-fit: cover;
      transition: transform 600ms $standard-easing;
    }
  }
  .second {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: $baseline-2;
    min-height: 153px;

    @include bp-sm {
      padding: $baseline-3;
    }

    .tag,
    .stock-counter,
    .time-counter,
    .reserved-space {
      height: 5.5 * $baseline;
    }
    .tag {
      margin: -2px 0 2px 0;
    }
    .time-counter {
      padding-top: 6px; // align with stock counter label
    }

    .pre-title {
      font-weight: 400;
      margin-bottom: 20px;

      @include bp-lg {
        margin-bottom: 12px;
      }
    }
    .label {
      margin-bottom: $baseline;
    }

    .description {
      color: $color-gray-tint-2;
      margin-bottom: $baseline; // margin between text and CTA should be 8px
      flex-grow: 1;

      &.empty {
        margin: 0;
      }
    }
  }
  &.auto .second {
    hyphens: auto;
  }

  // split, split-sidekick & standard
  &.split,
  &.split-sidekick,
  &.standard:where(.has-image) {
    background: $color-horizon;
    height: auto;
    flex-direction: column-reverse;
    background-position: center;
    --gradient-width: 100%;

    .first,
    .second {
      flex-basis: auto;
    }

    @include bp-md {
      --gradient-width: 55%;
      flex-direction: row-reverse;

      .first,
      .second {
        flex-basis: 50%;
        place-content: space-evenly;
      }
    }

    .second {
      padding: $baseline-3 $baseline-2 $baseline * 5;

      .description {
        color: $color-navy;

        ::slotted(ul) {
          // Icon list
          line-height: $baseline-4;
        }
      }
    }
    &.has-dark-background {
      .pre-title,
      .label,
      .description {
        color: $color-white;
      }
    }
    .image {
      border-radius: 0;
      object-fit: scale-down;
      height: 100%;
    }
    &.white {
      background: $color-white;

      &.border {
        border: 1px solid $color-gray-tint-4;
      }
    }
  }

  // gradient-sidekick & standard
  &.gradient-sidekick,
  &.standard.has-dark-background:not(.has-image) {
    border: 0;
    @include gradient-2;

    &.gradient-1 {
      @include gradient-1;
    }

    &.gradient-3 {
      @include gradient-3;
    }

    .first {
      display: none;
      visibility: hidden; // for e2e test
    }
    .pre-title,
    .label,
    .description {
      color: $color-white;
    }
  }

  // cover, cover-sidekick & standard
  &.cover,
  &.cover-sidekick,
  &.standard.has-bg-image:where(:not(.has-image)) {
    border: 0;
    background-position: center;
    --gradient-width: 100%;

    @include bp-sm {
      --gradient-width: 55%;
    }

    .first {
      display: none;
      visibility: hidden; // for e2e test
    }
    .label,
    .description {
      color: $color-white;
      overflow-wrap: break-word;

      ::slotted(ul) {
        line-height: $baseline-4;
      }
      @include bp-sm {
        max-width: min(55%, 340px);
      }
    }
  }

  // minimum height for split & cover
  &.split {
    min-height: 520px;

    @include bp-md {
      min-height: 0;
    }
  }
  &.cover {
    .second {
      min-height: 520px;
    }
  }
  &.split,
  &.cover {
    .second {
      @include bp-md {
        min-height: 480px;
      }
      @include bp-xl {
        min-height: 560px;
      }
    }
  }

  // minimum height for sidekicks
  &.split-sidekick,
  &.gradient-sidekick,
  &.cover-sidekick {
    .second {
      min-height: 340px;

      @include bp-md {
        min-height: 432px;
      }
    }
  }
  &.split-sidekick {
    min-height: 340px;

    @include bp-md {
      min-height: 432px;
    }

    .second {
      min-height: 0;
    }
  }

  // standard
  &.standard {
    border: 0;
    height: 100%;
    background: $color-horizon;

    &:not(.has-image) {
      .first {
        display: none;
        visibility: hidden; // for e2e test
      }
    }

    .ratio-1-1 {
      aspect-ratio: 1/1;
    }
    .ratio-16-9 {
      aspect-ratio: 16/9;
    }

    &.content-bottom,
    &.content-top {
      .first,
      .second {
        flex-basis: auto;
      }
    }
    &.content-bottom {
      flex-direction: column;
    }
    &.content-top {
      flex-direction: column-reverse;
    }

    &.gradient-1 {
      border: 0;
      @include gradient-1;
    }
    &.gradient-2 {
      border: 0;
      @include gradient-2;
    }
    &.gradient-3 {
      border: 0;
      @include gradient-3;
    }
    &.cloud {
      background: #f9f9f9; // .bg-cloud
    }
    &.white {
      background: $color-white;

      &.border {
        border: 1px solid $color-gray-tint-4;
      }
    }

    &.content-bottom,
    &.content-top {
      .label,
      .description {
        max-width: none;
        flex-grow: unset;
      }
    }
    &.content-bottom {
      --gradient-direction: 0deg;

      .label,
      .tag {
        margin-top: auto;
      }
      .tag + .label {
        margin-top: 0;
      }
      &.has-contained-image {
        .second {
          padding-top: 0;
        }
      }
    }
    &.content-top {
      --gradient-direction: 180deg;

      &.has-contained-image {
        .second {
          padding-bottom: 0;
        }
      }
    }
  }

  // padding for split/gradient/cover
  &.split,
  &.split-sidekick,
  &.gradient-sidekick,
  &.cover,
  &.cover-sidekick {
    .second {
      padding: $baseline-3 $baseline-2;

      @include bp-sm {
        padding: $baseline * 6 $baseline-4 $baseline-4;
      }
    }
  }

  // padding for standard & content-position
  // The content-position set to "left" is the default, which in mobile view is stacked vertically,
  // giving a larger padding-top. This is reversed when content-position "bottom" is applied,
  // placing the larger padding to padding-bottom.
  &.standard {
    .second,
    .first .link-wrapper {
      padding: $baseline-3 $baseline-2;
    }
  }
  &.standard.content-left:where(.has-image),
  &.standard.content-top:where(.has-image) {
    .second,
    .first .link-wrapper {
      padding: $baseline-3 $baseline-2 $baseline-2;
    }
  }
  &.standard.content-bottom:where(.has-image) {
    .second,
    .first .link-wrapper {
      padding: $baseline-2 $baseline-2 $baseline-3;
    }
  }
  &.standard,
  &.standard.content-left:where(.has-image),
  &.standard.content-top:where(.has-image),
  &.standard.content-bottom:where(.has-image) {
    .second,
    .first .link-wrapper {
      @include bp-sm {
        padding: $baseline-3; // padding around text should be 24px, not 32px
      }
    }
  }

  // products
  &.product,
  &.product-out-of-stock,
  &.product-button {
    .image {
      aspect-ratio: 1/1;
      object-fit: contain;
    }
    .description {
      @include typo-headline-4;
      @include font-semi-bold;
      color: $color-navy;
      margin-bottom: $baseline-3;
    }
    button {
      min-width: $button-min-width - 4px;
      min-height: $interaction-element-height;

      sdx-icon {
        margin-right: $baseline;
      }
    }

    @include bp-lg {
      .first {
        padding: $baseline-4;
      }
      .second {
        padding: 0 $baseline-4 $baseline * 5;
      }
      .label {
        margin-bottom: 0.5 * $baseline;
      }
      .link {
        opacity: 0;

        &.animated {
          transition: all 400ms $standard-easing;
        }
      }

      &:hover,
      &:focus {
        .link {
          opacity: 1;
        }
      }
    }

    // mobile & tablet
    @include bp-md-max {
      border-radius: 0;

      .image {
        transform: scale(1);
      }
      .label,
      .description {
        @include typo-headline-5;
        @include font-semi-bold;
      }
      .second {
        padding: $baseline-2 0 0;
      }

      &:hover,
      &:focus {
        border-color: transparent;
        box-shadow: none;
      }
    }

    &.product-out-of-stock {
      .image {
        opacity: 0.5;
      }
    }
  }
}

.component.sdx--dark-theme:not(
    .gradient-sidekick,
    .gradient-1,
    .gradient-2,
    .gradient-3
  ) {
  background-color: $color-dusk-tint-3-dark-theme;

  &.cloud {
    background-color: $color-dusk-tint-4-dark-theme;
  }

  @include bp-md-max {
    &.product,
    &.product-out-of-stock,
    &.product-button {
      background-color: transparent;
    }
  }

  // Disable box-shadows for all layouts
  &:hover {
    box-shadow: none;
  }

  &:hover,
  &:focus-visible {
    &.card {
      border-color: $color-gray-tint-4-dark-theme;
    }

    .link {
      @include link-hover-dark-theme;

      &.link--white {
        @include link-hover-dark;
      }
    }

    .button.button--secondary {
      @include button-hover(
        (
          button-color--active: rgb($color-int-blue2-dark-theme, 0.15),
          button-border-color--active: $color-int-blue2-dark-theme--active,
        )
      );
    }
  }

  .second {
    .label {
      color: $color-gray-dark-theme; // Override teaser link color
    }
    .description {
      color: $color-gray-tint-2-dark-theme;
    }
  }
}
